import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { fetchWorkflowsAndQueues } from '../api/workflowsAndQueues';
import { fetchWorkers } from '../api/workers';
import QueueWorkersContainer from '../components/QueueWorkersContainer';
import { useEnvironment } from '../utils/EnvironmentContext';
import { fetchCustomerAvailability } from '../api/customer-availability';
import {
  buildWorkflowQueuePriorityData,
  computeQueuesWithoutWorkers,
  filterWorkersForQueue,
  filterWorkflowNames,
  visibleWorkflowQueuePriorities
} from '../helpers/workflows';

export default function WorkflowViews() {
    const [showExplanation, setShowExplanation] = useState(false);
  const { env } = useEnvironment();

  const [loading, setLoading] = useState(true);
  const [taskQueues, setTaskQueues] = useState([]);
  const [workflows, setWorkflows] = useState([]);
  const [workflowQueuePriorities, setWorkflowQueuePriorities] = useState([]);

  const [selectedQueueSid, setSelectedQueueSid] = useState(null);
  const [teamFilter, setTeamFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const [allWorkers, setAllWorkers] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [workersLoading, setWorkersLoading] = useState(false);
  const [workersError, setWorkersError] = useState(null);
  const [customers, setCustomers] = useState([]);

  // Queues lacking workers
  const [queuesWithoutWorkers, setQueuesWithoutWorkers] = useState([]);

  // Load workflows and queues once
  useEffect(() => {
    async function loadData() {
      try {
        const { workflows, taskQueues } = await fetchWorkflowsAndQueues(env);

        const priorityData = buildWorkflowQueuePriorityData(workflows, taskQueues);
        setTaskQueues(taskQueues);
        setWorkflows(workflows);
        setWorkflowQueuePriorities(priorityData);
      } catch (err) {
        console.error('Failed to load workflows/queues', err.message);
      } finally {
        setLoading(false);
      }
    }

    loadData();
  }, [env]);

  useEffect(() => {
    async function loadCustomers() {
      try {
        const data = await fetchCustomerAvailability(env);
        setCustomers(data);
      } catch (err) {
        console.error('Failed to load customers:', err);
      }
    }
    loadCustomers();
  }, [env]);

  // Fetch all workers once on mount
  useEffect(() => {
    async function loadAllWorkers() {
      setWorkersLoading(true);
      setWorkersError(null);
      try {
        const fetched = await fetchWorkers(env);
        setAllWorkers(fetched);
      } catch (err) {
        console.error('Failed to fetch workers:', err.message);
        setWorkersError('Failed to fetch workers.');
      } finally {
        setWorkersLoading(false);
      }
    }

    loadAllWorkers();
  }, [env]);

  // Compute queues without assigned workers when dependencies change
  useEffect(() => {
    setQueuesWithoutWorkers(computeQueuesWithoutWorkers(taskQueues, allWorkers));
  }, [allWorkers, taskQueues]);

  // Recompute workers for selected queue
  useEffect(() => {
    const { workers: list, error } = filterWorkersForQueue(selectedQueueSid, taskQueues, allWorkers);
    setWorkers(list);
    setWorkersError(error);
  }, [selectedQueueSid, taskQueues, allWorkers]);

  const filteredWorkflowNames = filterWorkflowNames(workflows, teamFilter, searchTerm);
  const filteredWorkflowQueuePriorities = visibleWorkflowQueuePriorities(workflowQueuePriorities, filteredWorkflowNames);

    function LegendItem({ color, borderColor = 'transparent', label, boxStyle = 'circle' }) {
        return (
            <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-end', gap: 6}}>
                <div
                    style={{
                    width: 10,
                    height: 10,
                    borderRadius: boxStyle === 'circle' ? '50%' : '2px',
                    backgroundColor: color,
                    border: `2px solid ${borderColor}`,
                    }}
                />
                <span style={{ fontSize: 14 }}>{label}</span>
            </div>
        );
    }

  return (
    <div style={{ padding: 24, background: '#f7fafd', minHeight: '100vh' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 24, marginBottom: 32 }}>
        <div style={{ background: '#fff', borderRadius: 12, boxShadow: '0 2px 12px rgba(44,62,80,0.07)', padding: '12px 24px', display: 'flex', alignItems: 'center', gap: 12, flex: 1, minWidth: 260 }}>
          {/* Simple workflow icon (SVG) */}
          <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="2" y="2" width="8" height="8" rx="2" fill="#2d4a7a"/>
            <rect x="18" y="2" width="8" height="8" rx="2" fill="#2d4a7a"/>
            <rect x="10" y="18" width="8" height="8" rx="2" fill="#2d4a7a"/>
            <path d="M10 6h8M6 10v8M22 10v8M14 18v-4" stroke="#2d4a7a" strokeWidth="2" strokeLinecap="round"/>
          </svg>
          <h2
            className='task-events-title'
            style={{
              margin: 0,
              fontSize: 22,
              fontWeight: 600,
              color: '#2d4a7a',
              letterSpacing: 0.2,
            }}
          >
            Workflow & Servicegroep (Queue) Viewer
          </h2>
          <button
            aria-label="Toon uitleg grafiek"
            onClick={() => setShowExplanation(true)}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              marginLeft: 8,
              padding: 0,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="11" cy="11" r="11" fill="#2d4a7a"/>
              <text x="11" y="16" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#fff">?</text>
            </svg>
          </button>
              {/* Modal for chart explanation */}
              {showExplanation && createPortal(
                <div style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  width: '100vw',
                  height: '100vh',
                  background: 'rgba(44,62,80,0.18)',
                  zIndex: 9999,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  <div style={{
                    background: '#fff',
                    borderRadius: 14,
                    boxShadow: '0 4px 32px rgba(44,62,80,0.18)',
                    padding: '32px 28px',
                    maxWidth: 480,
                    width: '90%',
                    color: '#3a4a5a',
                    fontSize: 16,
                    fontWeight: 400,
                    position: 'relative',
                  }}>
                    <button
                      aria-label="Sluit uitleg"
                      onClick={() => setShowExplanation(false)}
                      style={{
                        position: 'absolute',
                        top: 16,
                        right: 16,
                        background: 'none',
                        border: 'none',
                        fontSize: 22,
                        color: '#2d4a7a',
                        cursor: 'pointer',
                      }}
                    >
                      &times;
                    </button>
                    <strong style={{ fontSize: 17, color: '#2d4a7a', display: 'block', marginBottom: 12 }}>Uitleg van de grafiek</strong>
                    <div>
                      Elke <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}><span style={{ width: 12, height: 12, borderRadius: '50%', background: 'rgba(54, 162, 235, 0.9)', display: 'inline-block', marginRight: 2, border: '2px solid transparent' }}></span> bol</span> staat voor een queue ( skill ) binnen een workflow.<br />
                      De x-as toont de workflow ( servicegroep ) namen, de y-as toont prioriteit en overflow niveaus.<br />
                      De kleur en het icoon van de bol geeft de status aan:<br />
                      <span style={{ display: 'block', marginTop: 16, color: '#2d4a7a', fontWeight: 500 }}>Klik op een bol om details van die queue te bekijken.</span>
                    </div>
                  </div>
                </div>,
                document.body
              )}
        </div>
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            gap: 24,
            alignItems: 'center',
            background: '#fff',
            borderRadius: 12,
            boxShadow: '0 2px 12px rgba(44,62,80,0.07)',
            padding: '12px 24px',
          }}
        >
          <div style={{ display: 'flex', gap: 18, alignItems: 'center' }}>
            <LegendItem color="rgba(54, 162, 235, 0.9)" label="Queue met agenten" />
            <LegendItem color="rgba(255, 99, 132, 0.9)" borderColor="darkred" label="Queue zonder agenten" />
            <LegendItem color="rgba(255, 206, 86, 1)" borderColor="black" label="Geselecteerde Queue" />
            <LegendItem color="rgba(184, 61, 61, 0.3)" label="Overflow Zone" boxStyle="solid" />
          </div>
          <div style={{ display: 'flex', gap: 18, alignItems: 'center' }}>
            <label style={{ fontWeight: 500, color: '#2d4a7a' }}>
              Team:
              <select
                value={teamFilter}
                onChange={e => setTeamFilter(e.target.value)}
                style={{
                  marginLeft: 8,
                  padding: '4px 10px',
                  borderRadius: 6,
                  border: '1px solid #e3eaf3',
                  background: '#f7fafd',
                  fontSize: 15,
                }}
              >
                <option value="all">All</option>
                <option value="fuse">Fuse</option>
                <option value="mass">Mass</option>
                <option value="sys">Sys</option>
                <option value="core">Core</option>
              </select>
            </label>
            <label style={{ fontWeight: 500, color: '#2d4a7a' }}>
              Search:
              <input
                type="text"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                placeholder="Zoek workflow of servicegroep"
                style={{
                  marginLeft: 8,
                  padding: '4px 10px',
                  borderRadius: 6,
                  border: '1px solid #e3eaf3',
                  background: '#f7fafd',
                  fontSize: 15,
                  minWidth: 160,
                }}
              />
            </label>
          </div>
        </div>
      </div>

      {loading ? (
        <p style={{ fontSize: 18, color: '#2d4a7a', marginTop: 40 }}>Loading workflows and queues...</p>
      ) : (
        <div style={{ display: 'flex', gap: 32, marginBottom: 32, alignItems: 'flex-start' }}>
          {/* Left column: Chart on top, Agent Skills below */}
          <div style={{ flex: 2, minWidth: 400, display: 'flex', flexDirection: 'column', gap: 32 }}>
            <div style={{ background: '#fff', borderRadius: 14, boxShadow: '0 2px 12px rgba(44,62,80,0.07)', padding: 24, marginBottom: 0 }}>
              {/* Chart container: place your chart component here, or ensure QueueWorkersContainer renders the chart at the top */}
              <QueueWorkersContainer
                env={env}
                queueData={filteredWorkflowQueuePriorities}
                selectedQueueSid={selectedQueueSid}
                onSelectQueue={queueSid => setSelectedQueueSid(queueSid)}
                workers={workers}
                loading={workersLoading}
                error={workersError}
                queuesWithoutWorkers={queuesWithoutWorkers.map(q => q.sid)}
                taskQueues={taskQueues}
                workflows={workflows}
                customers={customers}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
